import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Copy, 
  ExternalLink,
  Loader2,
  Server,
  Key,
  Link2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const LTISetup = () => {
  const navigate = useNavigate();
  const user = getUser();
  
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [toolConfig, setToolConfig] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    issuer: '',
    client_id: '',
    auth_login_url: '',
    auth_token_url: '',
    key_set_url: '',
    deployment_id: ''
  });

  useEffect(() => {
    if (!user || user.role !== 'super_admin') {
      navigate('/');
      return;
    }
    fetchPlatforms();
    fetchToolConfig();
  }, [user, navigate]);

  const fetchPlatforms = async () => {
    try {
      const response = await api.get('/lti/platforms');
      setPlatforms(response.data);
    } catch (err) {
      console.error('Failed to fetch platforms:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchToolConfig = async () => {
    try {
      const response = await api.get('/lti/config');
      setToolConfig(response.data);
    } catch (err) {
      console.error('Failed to fetch tool config:', err);
    }
  };

  const handleAddPlatform = async (e) => {
    e.preventDefault();
    
    try {
      await api.post('/lti/platforms', formData);
      toast.success('Platform registered successfully!');
      setShowAddModal(false);
      setFormData({
        name: '',
        issuer: '',
        client_id: '',
        auth_login_url: '',
        auth_token_url: '',
        key_set_url: '',
        deployment_id: ''
      });
      fetchPlatforms();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to register platform');
    }
  };

  const handleDeletePlatform = async (platformId, platformName) => {
    if (!window.confirm(`Delete "${platformName}"? This will break any existing LTI connections.`)) {
      return;
    }

    try {
      await api.delete(`/lti/platforms/${platformId}`);
      toast.success('Platform deleted');
      fetchPlatforms();
    } catch (err) {
      toast.error('Failed to delete platform');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const prefillMoodle = () => {
    const moodleUrl = formData.issuer || 'https://learning.myclasshub.org';
    setFormData(prev => ({
      ...prev,
      auth_login_url: `${moodleUrl}/mod/lti/auth.php`,
      auth_token_url: `${moodleUrl}/mod/lti/token.php`,
      key_set_url: `${moodleUrl}/mod/lti/certs.php`
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/super-admin/dashboard')}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">LTI Integration Setup</h1>
              <p className="text-sm text-slate-500">Connect MyExamDesk to Moodle and other LMS platforms</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Tool Configuration */}
        {toolConfig && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Key className="w-5 h-5 text-indigo-600" />
              MyExamDesk Tool Configuration
            </h2>
            <p className="text-sm text-slate-600 mb-4">
              Use these values when configuring MyExamDesk as an External Tool in Moodle:
            </p>
            
            <div className="grid gap-4">
              <ConfigRow 
                label="Tool URL" 
                value={toolConfig.target_link_uri}
                onCopy={copyToClipboard}
              />
              <ConfigRow 
                label="Initiate Login URL" 
                value={toolConfig.oidc_initiation_url}
                onCopy={copyToClipboard}
              />
              <ConfigRow 
                label="Redirect URI" 
                value={toolConfig.target_link_uri}
                onCopy={copyToClipboard}
              />
              <ConfigRow 
                label="Public Keyset URL" 
                value={toolConfig.public_jwk_url}
                onCopy={copyToClipboard}
              />
            </div>

            <div className="mt-6 p-4 bg-indigo-50 rounded-lg">
              <h3 className="font-medium text-indigo-900 mb-2">Moodle Setup Instructions:</h3>
              <ol className="list-decimal list-inside text-sm text-indigo-800 space-y-1">
                <li>Go to Site Administration → Plugins → External Tool → Manage Tools</li>
                <li>Click "Configure a tool manually"</li>
                <li>Enter the URLs above in the corresponding fields</li>
                <li>Set LTI version to "LTI 1.3"</li>
                <li>Enable "IMS LTI Assignment and Grade Services" for grade passback</li>
                <li>After saving, copy the Client ID, Platform ID, and Auth URLs back here</li>
              </ol>
            </div>
          </div>
        )}

        {/* Registered Platforms */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Server className="w-5 h-5 text-indigo-600" />
              Registered Platforms
            </h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Platform
            </button>
          </div>

          {platforms.length === 0 ? (
            <div className="text-center py-12">
              <Link2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">No LTI platforms registered yet</p>
              <p className="text-sm text-slate-400 mt-1">
                Add your Moodle instance to enable LTI integration
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {platforms.map(platform => (
                <div
                  key={platform.id}
                  className="p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-slate-900">{platform.name}</h3>
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                          Active
                        </span>
                      </div>
                      <p className="text-sm text-slate-500">{platform.issuer}</p>
                      <p className="text-xs text-slate-400 mt-1">Client ID: {platform.client_id}</p>
                    </div>
                    <button
                      onClick={() => handleDeletePlatform(platform.id, platform.name)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Platform Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Register LTI Platform</h2>
              <p className="text-sm text-slate-500 mt-1">
                Enter the details from your Moodle LTI tool configuration
              </p>
            </div>

            <form onSubmit={handleAddPlatform} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Platform Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., MyClassHub Moodle"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Platform ID / Issuer URL
                </label>
                <input
                  type="url"
                  required
                  value={formData.issuer}
                  onChange={(e) => setFormData({ ...formData, issuer: e.target.value })}
                  onBlur={prefillMoodle}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="https://learning.myclasshub.org"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Client ID (from Moodle)
                </label>
                <input
                  type="text"
                  required
                  value={formData.client_id}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Copy from Moodle tool configuration"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Authentication Request URL
                </label>
                <input
                  type="url"
                  required
                  value={formData.auth_login_url}
                  onChange={(e) => setFormData({ ...formData, auth_login_url: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="https://your-moodle.com/mod/lti/auth.php"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Access Token URL
                </label>
                <input
                  type="url"
                  required
                  value={formData.auth_token_url}
                  onChange={(e) => setFormData({ ...formData, auth_token_url: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="https://your-moodle.com/mod/lti/token.php"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Public Keyset URL
                </label>
                <input
                  type="url"
                  required
                  value={formData.key_set_url}
                  onChange={(e) => setFormData({ ...formData, key_set_url: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="https://your-moodle.com/mod/lti/certs.php"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Deployment ID (optional)
                </label>
                <input
                  type="text"
                  value={formData.deployment_id}
                  onChange={(e) => setFormData({ ...formData, deployment_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Usually '1' for single deployment"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Register Platform
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const ConfigRow = ({ label, value, onCopy }) => (
  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-sm font-mono text-slate-900 break-all">{value}</p>
    </div>
    <button
      onClick={() => onCopy(value, label)}
      className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors flex-shrink-0 ml-2"
    >
      <Copy className="w-4 h-4" />
    </button>
  </div>
);

export default LTISetup;
